import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Rocket,
  Search,
  Target,
  LineChart,
  CheckCircle2,
  BarChart3,
  Megaphone,
  ArrowRight,
  BookOpen,
  HelpCircle,
  Mail,
  MessageSquare,
} from 'lucide-react';
import { API_URLS } from '../../config/api';

const HERO_IMAGE_URL = 'https://images.unsplash.com/photo-1522199710521-72d69614c702?auto=format&fit=crop&w=900&q=80';
const AD_IMAGE_URL = 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=900&q=80';

const normalize = (s) => (s || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/ı/g,'i')
  .replace(/i̇/g,'i')
  .replace(/ş/g,'s')
  .replace(/ğ/g,'g')
  .replace(/ç/g,'c')
  .replace(/ö/g,'o')
  .replace(/ü/g,'u')
  .replace(/\s+/g, ' ');

const matchesReklam = (val) => {
  const n = normalize(val);
  return n === 'reklam' || 
         n === 'pazarlama' ||
         n === 'reklam-hizmetleri' ||
         n === 'reklam hizmetleri' ||
         n === 'dijital pazarlama' ||
         n === 'dijital-pazarlama' ||
         n.includes('reklam') || 
         n.includes('pazarlama');
};

const seoHighlights = [
  {
    title: 'Teknik SEO Analizi',
    description:
      'Site hızından indekslemeye kadar tüm teknik detayları analiz ediyor, arama motorlarının sizi en doğru şekilde taramasını sağlıyoruz.',
    icon: <Search className="w-10 h-10 text-rose-600" />,
  },
  {
    title: 'İçerik Stratejisi',
    description:
      'Dönüşüm odaklı anahtar kelime araştırmaları ile SEO uyumlu blog ve sayfa içerikleri planlıyoruz.',
    icon: <Megaphone className="w-10 h-10 text-rose-600" />,
  },
  {
    title: 'Otorite Oluşturma',
    description:
      'Backlink, dijital PR ve yerel SEO çalışmaları ile markanızın otoritesini güçlendiriyoruz.',
    icon: <BarChart3 className="w-10 h-10 text-rose-600" />,
  },
  {
    title: 'Dönüşüm Optimizasyonu',
    description:
      'Lead toplama formlarından satış hunisine kadar tüm temas noktalarını analiz ederek dönüşüm oranlarınızı artırıyoruz.',
    icon: <Rocket className="w-10 h-10 text-rose-600" />,
  },
];

const advertisingChannels = [
  {
    title: 'Google Ads Yönetimi',
    description:
      'Arama ağı, görüntülü reklamlar, alışveriş kampanyaları ve YouTube reklamları ile doğru kitlelere ulaşıyoruz.',
  },
  {
    title: 'Meta Reklamları',
    description:
      'Facebook, Instagram ve WhatsApp kanalında performans odaklı kampanyalar kurguluyoruz.',
  },
  {
    title: 'LinkedIn ve B2B Kampanyaları',
    description:
      'Kurumsal hedef kitlelere yönelik yüksek potansiyelli reklam stratejileri oluşturuyoruz.',
  },
  {
    title: 'Remarketing Stratejileri',
    description:
      'Ziyaretçilerinizi yeniden hedefleyerek dönüşüm oranlarını ikiye katlıyoruz.',
  },
];

const processSteps = [
  {
    title: '1. Keşif ve Hedef Belirleme',
    description:
      'Mevcut durum analizi ile iş hedeflerinizi, bütçenizi ve rekabet ortamını birlikte inceliyoruz.',
  },
  {
    title: '2. Strateji ve Planlama',
    description:
      'SEO ve reklam çalışmalarının hangi aşamalarla ilerleyeceğini, KPI ve raporlama takvimini kararlaştırıyoruz.',
  },
  {
    title: '3. Uygulama ve Optimizasyon',
    description:
      'Reklam kampanyalarını başlatıyor, içerik ve teknik SEO iyileştirmelerini hayata geçiriyoruz.',
  },
  {
    title: '4. Ölçümleme ve Büyüme',
    description:
      'Haftalık ve aylık raporlar ile sonuçları değerlendiriyor, yeni büyüme fırsatlarına odaklanıyoruz.',
  },
];

const guarantees = [
  'Şeffaf raporlama ve düzenli iletişim',
  'Performansa göre sürekli optimizasyon',
  'Deneyimli SEO ve reklam uzmanlarından destek',
  'Sektöre özel strateji ve içerik üretimi',
  '360 derece pazarlama yaklaşımı',
];


const marketingFaqs = [
  {
    question: 'SEO çalışmalarından ne kadar sürede sonuç alırım?',
    answer:
      'Teknik altyapı, sektör rekabeti ve içerik hacmine göre değişmekle birlikte ilk 3 ayda görünürlük artışı, 6 ay ve sonrasında kalıcı sıralama kazanımları görülür.',
  },
  {
    question: 'Reklam bütçemi nasıl belirlemeliyim?',
    answer:
      'Aylık hedeflediğiniz lead veya satış adedi, ortalama dönüşüm oranı ve tıklama başına maliyetler baz alınarak minimum verimli bütçeyi birlikte planlıyoruz.',
  },
  {
    question: 'SEO ve reklam çalışmalarını aynı anda yürütmek avantajlı mı?',
    answer:
      'Evet. SEO ile organik görünürlüğünüz artarken, paralel yürüyen reklam kampanyaları hızlı geri dönüş sağlar ve remarketing kitleleri oluşturur.',
  },
  {
    question: 'Raporlama ve iletişim süreci nasıl ilerliyor?',
    answer:
      'Haftalık performans özetleri, aylık detaylı raporlar ve aylık strateji toplantıları ile tüm süreci şeffaf şekilde paylaşıyoruz.',
  },
];

export default function MarketingServicesPage() {
  const [ready, setReady] = useState(false);
  const [marketingArticles, setMarketingArticles] = useState([]);
  const whatsappNumber = '905324404011';
  const whatsappMessage = encodeURIComponent('Merhaba, pazarlama hizmetleriniz hakkında bilgi almak istiyorum.');

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // Fetch reklam category blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        // Try server-side filtered fetch first with different category slug variations
        const categorySlugs = ['reklam', 'pazarlama', 'reklam-hizmetleri'];
        let list = [];
        
        for (const slug of categorySlugs) {
          const url = `${API_URLS.BLOGS}&category=${slug}&limit=3`;
          const res = await fetch(url);
          const txt = await res.text();
          let data; 
          try { 
            data = JSON.parse(txt); 
          } catch (e) { 
            data = { status: 'error', blogs: [] }; 
          }
          
          // Handle different response formats (same as BlogPage.jsx)
          list = Array.isArray(data) ? data : (data.blogs || data.data || []);
          
          if (list && list.length > 0) {
            break;
          }
        }
        
        // Fallback: fetch all then client-side filter if empty
        if (!list || list.length === 0) {
          const allRes = await fetch(API_URLS.BLOGS);
          const allTxt = await allRes.text();
          let allData; 
          try { 
            allData = JSON.parse(allTxt); 
          } catch (e) { 
            allData = { status: 'error', blogs: [] }; 
          }
          
          // Handle different response formats (same as BlogPage.jsx)
          const allList = Array.isArray(allData) ? allData : (allData.blogs || allData.data || []);
          
          list = allList.filter(b => {
            // Check category field (slug)
            const categorySlug = b.category || '';
            // Check category_name field
            const categoryName = b.category_name || '';
            
            return matchesReklam(categorySlug) || matchesReklam(categoryName);
          }).slice(0, 3);
        }
        
        const filtered = list
          .filter(b => b.is_published === 1 || b.is_published === true || b.is_published === '1' || b.is_published === undefined)
          .sort((a,b) => {
            const dateA = new Date(a.published_at || a.created_at || a.createdAt || 0);
            const dateB = new Date(b.published_at || b.created_at || b.createdAt || 0);
            return dateB - dateA || (b.id||0) - (a.id||0);
          })
          .slice(0, 3)
          .map(b => ({
            id: b.id || b.slug,
            title: b.title || 'Blog',
            excerpt: b.excerpt || b.summary || '',
            link: b.slug ? `/blog/${b.slug}` : `/blog/${b.id || ''}`,
            tag: b.category_name || b.category || 'Reklam',
            image: b.image || b.featured_image || b.thumbnail || b.cover_image || 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=800&q=80'
          }));
        
        setMarketingArticles(filtered);
      } catch (e) {
        console.error('Error fetching marketing blogs:', e);
        setMarketingArticles([]);
      }
    };
    
    fetchBlogs();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-rose-50">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-br from-rose-600 via-rose-700 to-rose-900 py-20 overflow-hidden"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="w-full lg:w-1/2 text-white text-left">
              <Badge className="bg-white/20 text-white uppercase tracking-widest mb-4">
                SEO & Reklam Hizmetleri
              </Badge>
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6">
                Pazarlama Bütçenizi Sonuç Odaklı Şekilde Yönetin
              </h1>
              <p className="text-xl text-rose-100 max-w-xl">
                Arama motoru optimizasyonu ve dijital reklam yönetimi ile müşteri kazanımınızı hızlandırıyor, gelirlerinizi sürdürülebilir şekilde artırıyoruz.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button size="lg" className="bg-white text-rose-700 hover:bg-rose-100">
                  Ücretsiz Strateji Görüşmesi Alın
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                >
                  Hizmet Detayı Talep Et
                </Button>
              </div>
            </div>
            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-4 bg-white/10 blur-3xl rounded-full"></div>
                <img
                  src={HERO_IMAGE_URL}
                  alt="Pazarlama ekibi çalışma"
                  className="relative w-full rounded-3xl shadow-2xl object-cover border border-white/10"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Highlights */}
      <section
        id="seo"
        className="py-16"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.15s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <Badge className="bg-rose-100 text-rose-700">SEO Odaklı Yaklaşım</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
                Arama Sonuçlarında İlk Sırada Yer Alın
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Uzman SEO ekibimiz; teknik altyapıdan içerik üretimine, backlink stratejisinden dönüşüm hunisine kadar tüm süreci uçtan uca yönetir. Amaç yalnızca trafik değil, sürdürülebilir gelir artışıdır.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-600 mt-1" />
                  <span className="text-gray-700">Anahtar kelime araştırması ve SERP analizi</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-600 mt-1" />
                  <span className="text-gray-700">Teknik SEO iyileştirmeleri ve Core Web Vitals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-rose-600 mt-1" />
                  <span className="text-gray-700">SEO uyumlu içerik takvimi ve yayın yönetimi</span>
                </li>
              </ul>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {seoHighlights.map((highlight) => (
                <Card key={highlight.title} className="border-rose-200 shadow-sm">
                  <CardContent className="p-6 space-y-4">
                    <div>{highlight.icon}</div>
                    <h3 className="text-lg font-semibold text-gray-900">{highlight.title}</h3>
                    <p className="text-sm text-gray-600">{highlight.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advertising */}
      <section
        id="reklam"
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.25s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="w-full lg:w-1/2 text-left">
              <Badge className="bg-rose-100 text-rose-700">Reklam Yönetimi</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
                Doğru Kitle, Doğru Mesaj, Maksimum Dönüşüm
              </h2>
              <p className="text-lg text-gray-600">
                Performans odaklı reklam ekiplerimiz bütçenizi akıllı şekilde kullanır. Her bir kanal için hedef kitle, teklif yönetimi ve içerik optimizasyonunu sürekli test ederiz.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-rose-50 border border-rose-100">
                  <h3 className="text-base font-semibold text-rose-700 mb-2">Kanal Bazlı Strateji</h3>
                  <p className="text-sm text-gray-600">Google, Meta ve LinkedIn kampanyalarını ayrı KPI’larla yönetiyoruz.</p>
                </div>
                <div className="p-5 rounded-xl bg-rose-50 border border-rose-100">
                  <h3 className="text-base font-semibold text-rose-700 mb-2">Sürekli Optimizasyon</h3>
                  <p className="text-sm text-gray-600">A/B testleri ve otomatik kurallar ile kampanya maliyetlerini kontrol altında tutuyoruz.</p>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-4 bg-rose-100 blur-3xl rounded-full"></div>
                <img
                  src={AD_IMAGE_URL}
                  alt="Dijital reklam yönetimi"
                  className="relative w-full rounded-3xl shadow-xl object-cover border border-rose-100"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advertisingChannels.map((channel) => (
              <Card key={channel.title} className="border-rose-200 hover:border-rose-400 transition-colors duration-200">
                <CardContent className="p-5 space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">{channel.title}</h3>
                  <p className="text-sm text-gray-600">{channel.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section
        className="py-16"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.35s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-1/3">
              <Badge className="bg-rose-100 text-rose-700">Çalışma Modelimiz</Badge>
              <h2 className="text-3xl font-bold text-gray-900 mt-4 mb-4">
                Şeffaf ve Ölçülebilir Süreçler
              </h2>
              <p className="text-gray-600">
                Pazarlama çalışmalarını adım adım ilerletiyor, her aşamada net hedefler ve KPI&apos;lar tanımlıyoruz. Size özel bir ekip kurarak tek bir kanal yerine tüm huniyi birlikte yönetiyoruz.
              </p>
              <div className="mt-6 space-y-3">
                {guarantees.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rose-600 mt-0.5" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1 grid sm:grid-cols-2 gap-6">
              {processSteps.map((step) => (
                <Card key={step.title} className="border-rose-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6 space-y-3">
                    <Badge className="bg-rose-50 text-rose-600 w-max">{step.title}</Badge>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Metrics CTA */}
      <section
        className="py-16 bg-gradient-to-br from-rose-600 via-rose-700 to-rose-900"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.45s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center text-white">
            <div>
              <Badge className="bg-white/10 text-white uppercase tracking-widest">
                Neden Biz?
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
                Pazarlama yatırımlarınızdan daha fazla getiri alın
              </h2>
              <p className="text-rose-100 text-lg leading-relaxed">
                SEO ve reklam hizmetlerimizi tek çatı altında topluyor, satış hunisi boyunca bütünsel performans ölçümü sağlıyoruz. Her ay strateji toplantıları ile yeni büyüme fırsatları sunuyoruz.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <Button className="bg-white text-rose-700 hover:bg-rose-100">
                  Kampanya Başlat
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  Referanslarımızı İncele
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="bg-white border border-rose-100 shadow-lg">
                <CardContent className="p-6 space-y-2 text-gray-700">
                  <LineChart className="w-10 h-10 text-rose-600" />
                  <div className="text-3xl font-extrabold text-gray-900">%35+</div>
                  <p className="text-sm text-gray-600">Ortalama organik trafik artışı</p>
                </CardContent>
              </Card>
              <Card className="bg-white border border-rose-100 shadow-lg">
                <CardContent className="p-6 space-y-2 text-gray-700">
                  <Target className="w-10 h-10 text-rose-600" />
                  <div className="text-3xl font-extrabold text-gray-900">%120</div>
                  <p className="text-sm text-gray-600">Remarketing kampanyalarında dönüşüm artışı</p>
                </CardContent>
              </Card>
              <Card className="bg-white border border-rose-100 shadow-lg">
                <CardContent className="p-6 space-y-2 text-gray-700">
                  <Rocket className="w-10 h-10 text-rose-600" />
                  <div className="text-3xl font-extrabold text-gray-900">120+</div>
                  <p className="text-sm text-gray-600">Aktif yönetilen kampanya</p>
                </CardContent>
              </Card>
              <Card className="bg-white border border-rose-100 shadow-lg">
                <CardContent className="p-6 space-y-2 text-gray-700">
                  <CheckCircle2 className="w-10 h-10 text-rose-600" />
                  <div className="text-3xl font-extrabold text-gray-900">7/24</div>
                  <p className="text-sm text-gray-600">Uzman desteği ve izleme</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Highlights */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.5s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
            <div>
              <Badge className="bg-rose-100 text-rose-700">Blog</Badge>
              <h2 className="text-3xl font-bold text-gray-900 mt-3">
                Pazarlama Ekipleri İçin Güncel İçerikler
              </h2>
              <p className="text-gray-600 mt-2 max-w-2xl">
                Strateji, içerik ve performans pazarlamasına dair en yeni rehberlerimizi ve vaka çalışmalarımızı inceleyin.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-rose-200 text-rose-600 hover:bg-rose-50"
              onClick={() => window.open('/blog', '_blank')}
            >
              Tüm Yazıları Gör
            </Button>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {marketingArticles.length > 0 ? marketingArticles.map((article) => (
              <Card key={article.title || article.id} className="overflow-hidden border-rose-100 hover:shadow-lg transition-shadow">
                {article.image && (
                  <div className="aspect-[16/9] bg-rose-50 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <CardContent className="p-5 space-y-3">
                  <div className="inline-flex items-center px-3 py-1 bg-rose-50 text-rose-600 text-xs font-semibold rounded-full">
                    <BookOpen className="w-3.5 h-3.5 mr-1" />
                    {article.tag}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{article.excerpt}</p>
                  <button
                    onClick={() => window.open(article.link, '_blank')}
                    className="inline-flex items-center text-sm font-semibold text-rose-600 hover:text-rose-700"
                  >
                    Yazıyı Oku
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </CardContent>
              </Card>
            )) : (
              <div className="col-span-3 text-center py-8 text-gray-500">
                Henüz blog yazısı eklenmemiş.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-16 bg-rose-50"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.53s' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <Badge className="bg-rose-200 text-rose-800">Sıkça Sorulan Sorular</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mt-3">Pazarlama Hizmetleri Hakkında Merak Edilenler</h2>
            <p className="text-gray-600 mt-2">
              Strateji, süreç ve raporlama hakkında en çok aldığımız soruları sizin için yanıtladık.
            </p>
          </div>

          <div className="space-y-4">
            {marketingFaqs.map((faq, index) => (
              <details
                key={faq.question}
                className="group rounded-2xl bg-white border border-rose-100 px-6 py-5 shadow-sm"
                open={index === 0}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <div className="flex items-center text-left">
                    <HelpCircle className="w-5 h-5 text-rose-500 mr-3" />
                    <span className="text-base font-semibold text-gray-900 group-open:text-rose-600">
                      {faq.question}
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-rose-400 transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-gray-600">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-20 bg-gradient-to-r from-rose-600 to-rose-700 text-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.55s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">Pazarlama Stratejiniz İçin Bize Ulaşın</h2>
            <p className="text-xl text-rose-100 mb-8 max-w-3xl mx-auto">
              SEO ve reklam yatırımlarınızı tek çatı altında toplayarak ölçülebilir sonuçlar üretelim. Sektörünüze özel stratejilerimizi ve başarı hikâyelerimizi sizinle paylaşalım.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-rose-700 rounded-lg hover:bg-rose-50 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                Strateji Talep Et
              </a>
              <a
                href={`https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Uzmanla Görüş
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Target className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Stratejik Planlama</h3>
                <p className="text-rose-100">Hedef kitlenize özel çok kanallı kampanyalar</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <BarChart3 className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Aylık Raporlama</h3>
                <p className="text-rose-100">Şeffaf performans raporları ve KPI takibi</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Megaphone className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Yaratıcı İçerik</h3>
                <p className="text-rose-100">Markanıza uygun kampanya mesajları ve içerik üretimi</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

